import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function ConstructionPage() {
  const { t } = useTranslation();

  useSEO({
    title: 'İnşaat Hizmetleri | Canel Elektrik – Anahtar Teslim Endüstriyel İnşaat İstanbul',
    description:
      "Kaba inşaattan ince işlere, dış mekan uygulamalarından proje yönetimine kadar anahtar teslim endüstriyel inşaat hizmetleri. İstanbul merkezli, Türkiye genelinde uygulama yapan Canel Elektrik.",
    keywords:
      'anahtar teslim inşaat İstanbul, endüstriyel inşaat taahhüt, kaba inşaat betonarme, proje yönetimi Türkiye',
    canonical: '/construction',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'İnşaat Hizmetleri – Canel Elektrik',
      url: `${SITE_URL}/construction`,
      description:
        'Temelden çatıya anahtar teslim endüstriyel inşaat çözümleri. Kaba inşaat, ince işler, dış mekan ve proje yönetimi.',
      provider: {
        '@type': 'Organization',
        name: 'Canel Elektrik İnşaat Taahhüt Mühendislik',
        url: SITE_URL,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Perpa Ticaret Merkezi, A Blok Kat: 5 No: 204',
          addressLocality: 'Şişli',
          addressRegion: 'İstanbul',
          addressCountry: 'TR',
        },
      },
      serviceType: 'Endüstriyel İnşaat Taahhüt',
      areaServed: {
        '@type': 'Country',
        name: 'Türkiye',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'İnşaat Hizmetleri',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kaba İnşaat' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'İnce İşler & Mimari' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dış Mekan İşleri' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Proje Yönetimi' } },
        ],
      },
    },
  });

  const services = [
    {
      icon: 'ri-building-2-line',
      titleKey: 'svc_rough_title',
      descKey: 'svc_rough_desc',
      detailKeys: ['con_detail_rough1', 'con_detail_rough2', 'con_detail_rough3', 'con_detail_rough4', 'con_detail_rough5', 'con_detail_rough6'],
      image: 'https://readdy.ai/api/search-image?query=reinforced%20concrete%20construction%20foundation%20industrial%20building%20Turkey%20professional%20engineering%20site%20workers&width=600&height=380&seq=con1&orientation=landscape',
    },
    {
      icon: 'ri-paint-brush-line',
      titleKey: 'svc_finish_title',
      descKey: 'svc_finish_desc',
      detailKeys: ['con_detail_finish1', 'con_detail_finish2', 'con_detail_finish3', 'con_detail_finish4', 'con_detail_finish5', 'con_detail_finish6'],
      image: 'https://readdy.ai/api/search-image?query=interior%20finishing%20construction%20work%20flooring%20painting%20modern%20industrial%20building%20Turkey%20professional&width=600&height=380&seq=con2&orientation=landscape',
    },
    {
      icon: 'ri-landscape-line',
      titleKey: 'svc_ext_title',
      descKey: 'svc_ext_desc',
      detailKeys: ['con_detail_ext1', 'con_detail_ext2', 'con_detail_ext3', 'con_detail_ext4', 'con_detail_ext5', 'con_detail_ext6'],
      image: 'https://readdy.ai/api/search-image?query=exterior%20facade%20cladding%20insulation%20modern%20industrial%20building%20Turkey%20professional%20construction%20architecture&width=600&height=380&seq=con3&orientation=landscape',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      titleKey: 'svc_pm_title',
      descKey: 'svc_pm_desc',
      detailKeys: ['con_detail_pm1', 'con_detail_pm2', 'con_detail_pm3', 'con_detail_pm4', 'con_detail_pm5', 'con_detail_pm6'],
      image: 'https://readdy.ai/api/search-image?query=project%20management%20construction%20site%20meeting%20engineers%20Turkey%20professional%20planning%20scheduling%20industrial&width=600&height=380&seq=con4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 md:h-80 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=large%20industrial%20construction%20site%20Turkey%20professional%20building%20workers%20crane%20steel%20structure%20modern%20facility&width=1920&height=500&seq=con-hero&orientation=landscape"
          alt="Construction Services"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2332]/80 to-[#1A2332]/40" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto w-full">
          <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase mb-3">{t('services_construction_title')}</span>
          <h1 className="text-3xl md:text-5xl font-black text-white">{t('services_construction_title')}</h1>
          <p className="text-white/70 mt-3 text-base max-w-xl">{t('con_hero_subtitle')}</p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-20">
          {services.map((svc, idx) => (
            <div key={svc.titleKey} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-5">
                  <i className={`${svc.icon} text-[#E8A020] text-2xl`} />
                </div>
                <h2 className="text-2xl font-black text-[#1A2332] mb-4">{t(svc.titleKey)}</h2>
                <p className="text-gray-500 text-base leading-relaxed mb-6">{t(svc.descKey)}</p>
                <ul className="grid grid-cols-2 gap-2">
                  {svc.detailKeys.map((dk) => (
                    <li key={dk} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#E8A020]" /> {t(dk)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`w-full h-72 rounded-lg overflow-hidden ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img src={svc.image} alt={t(svc.titleKey)} className="w-full h-full object-cover object-top" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A2332]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">{t('cta_title')}</h2>
          <p className="text-white/60 mb-8">{t('cta_desc')}</p>
          <a href="/contact" className="bg-[#E8A020] text-white font-bold px-8 py-4 rounded-full hover:bg-[#d4911a] transition-colors cursor-pointer whitespace-nowrap inline-block">
            {t('nav_quote')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
